import { useCallback, useLayoutEffect, useMemo, useRef } from "react";
import { type StateSnapshot, type VirtuosoHandle } from "react-virtuoso";

export function clearVirtuosoScrollRestoration(key: string): void {
  sessionStorage.removeItem(key);
}

interface UseVirtuosoScrollRestorationOptions {
  /**
   * Automatically save scroll state when the component unmounts.
   * Set to false when you need to control save timing manually
   * using the returned `saveScrollPosition` function.
   * @default true
   */
  saveOnUnmount?: boolean;
}

export function useVirtuosoScrollRestoration(
  key: string,
  options: UseVirtuosoScrollRestorationOptions = {}
) {
  const { saveOnUnmount = true } = options;

  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const restoreStateFrom = useMemo<StateSnapshot | undefined>(() => {
    const saved = sessionStorage.getItem(key);
    if (saved) {
      sessionStorage.removeItem(key);
      try {
        return JSON.parse(saved) as StateSnapshot;
      } catch {
        return undefined;
      }
    }

    return undefined;
  }, [key]);

  const saveScrollPosition = useCallback(() => {
    virtuosoRef.current?.getState((state) => {
      sessionStorage.setItem(key, JSON.stringify(state));
    });
  }, [key]);

  // useLayoutEffect because useEffect cleanup runs after refs are detached.
  // virtuosoRef.current accessed directly because Virtuoso recreates its handle.
  useLayoutEffect(() => {
    if (!saveOnUnmount) {
      return;
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Virtuoso recreates its handle, so capturing at mount time gives a stale reference
      virtuosoRef.current?.getState((state) => {
        sessionStorage.setItem(key, JSON.stringify(state));
      });
    };
  }, [key, saveOnUnmount]);

  return { virtuosoRef, restoreStateFrom, saveScrollPosition };
}
